import * as React from 'react';
import {
    Checkbox,
    CommandBar,
    DefaultButton,
    DetailsList,
    DetailsListLayoutMode,
    Dialog,
    DialogFooter,
    DialogType,
    Dropdown,
    IDropdownOption,
    IColumn,
    ICommandBarItemProps,
    IconButton,
    Label,
    MessageBar,
    MessageBarType,
    Panel,
    PanelType,
    PrimaryButton,
    Selection,
    SelectionMode,
    Separator,
    Spinner,
    SpinnerSize,
    Stack,
    Text,
    TextField,
} from '@fluentui/react';
import { ApiHelper } from '../../../ApiHelper';
import {
    AgentTaskSnapshotSummary,
    BatchUpdateAgentTaskWorkItemsResult,
    formatCompactDate,
} from '../shared';

export function BatchUpdateWorkItemsPanel(props: {
    isOpen: boolean;
    serviceName: string;
    taskListId: number;
    snapshot?: AgentTaskSnapshotSummary | null;
    taskIds: number[];
    onDismiss: () => void;
    onUpdated: (result: BatchUpdateAgentTaskWorkItemsResult) => void;
}) {
    const { isOpen, serviceName, taskListId, snapshot, taskIds, onDismiss, onUpdated } = props;
    const statusOptions = React.useMemo<IDropdownOption[]>(() => [
        { key: 4, text: 'Published' },
        { key: 5, text: 'Blocked' },
        { key: 6, text: 'Signed off' },
        { key: 7, text: 'Completed' },
        { key: 8, text: 'Abandoned' },
    ], []);
    const [selectedStatus, setSelectedStatus] = React.useState<number>(4);
    const [submitting, setSubmitting] = React.useState(false);
    const [submitError, setSubmitError] = React.useState<string | null>(null);

    React.useEffect(() => {
        if (!isOpen) {
            return;
        }

        setSelectedStatus(4);
        setSubmitError(null);
    }, [isOpen]);

    async function handleSubmit() {
        setSubmitting(true);
        setSubmitError(null);

        try {
            if (!snapshot) {
                throw new Error('Select a snapshot before updating work items.');
            }

            const result = await ApiHelper(`/api/service/${serviceName}/agenttasklists/${taskListId}/workitems:batchUpdate`, 'POST', {
                taskIds,
                snapshotId: snapshot.snapshotId,
                pullRequestStatus: selectedStatus,
            }) as BatchUpdateAgentTaskWorkItemsResult;

            onUpdated(result);
        } catch (err) {
            setSubmitError(err instanceof Error ? err.message : 'Failed to update work items.');
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <Panel
            isOpen={isOpen}
            onDismiss={onDismiss}
            type={PanelType.medium}
            headerText="Update selected PR status"
        >
            <Stack tokens={{ childrenGap: 16 }}>
                {submitError && (
                    <MessageBar messageBarType={MessageBarType.error}>{submitError}</MessageBar>
                )}

                <Text>
                    Apply the same PR status to {taskIds.length} selected task{taskIds.length === 1 ? '' : 's'}.
                </Text>

                <Dropdown
                    label="New PR status"
                    options={statusOptions}
                    selectedKey={selectedStatus}
                    onChange={(_, option) => setSelectedStatus(Number(option?.key))}
                    disabled={submitting}
                />

                {snapshot && (
                    <Text styles={{ root: { color: '#605e5c' } }}>
                        Snapshot {snapshot.name || `#${snapshot.snapshotId}`} • {formatCompactDate(snapshot.date)}
                    </Text>
                )}

                <Stack horizontal tokens={{ childrenGap: 8 }}>
                    <PrimaryButton
                        text={submitting ? 'Updating...' : 'Update PR status'}
                        onClick={handleSubmit}
                        disabled={submitting || !snapshot || taskIds.length === 0}
                    />
                    <DefaultButton text="Cancel" onClick={onDismiss} disabled={submitting} />
                </Stack>
            </Stack>
        </Panel>
    );
}

